import { injectable } from 'inversify';
import { nanoid } from '@reduxjs/toolkit';

import type { FlowNode, FlowSchema } from '../../types/event-types';

import { EventTypesFlowSchemaService } from './flow-schema.service';

@injectable()
export class ParallelGroupService {
  constructor(
    private readonly flowSchemaService: EventTypesFlowSchemaService,
  ) {}

  private createGroupId() {
    return `Group_${nanoid()}`;
  }

  private getGroupNodes(schema: FlowSchema, groupId: string): FlowNode[] {
    return schema.nodes.filter((node) => node.parallelGroupId === groupId);
  }

  findSystemTaskBeforeGroup(
    schema: FlowSchema,
    groupId: string,
  ): FlowNode | null {
    const groupNodes = this.getGroupNodes(schema, groupId);
    const groupNodeIds = new Set(groupNodes.map(({ id }) => id));

    if (!groupNodeIds.size) {
      return null;
    }

    const firstExternalIncomingEdge = schema.edges.find(
      (edge) =>
        groupNodeIds.has(edge.targetId) && !groupNodeIds.has(edge.sourceId),
    );

    if (!firstExternalIncomingEdge) {
      return null;
    }

    const sourceNode = this.flowSchemaService.getNodeById(
      schema,
      firstExternalIncomingEdge.sourceId,
    );

    return sourceNode?.type === 'SystemTask' ? sourceNode : null;
  }

  findOrCreateSystemTaskBeforeGroup(
    schema: FlowSchema,
    groupId: string,
  ): { schema: FlowSchema; systemTaskId: string | null } {
    const existingSystemTask = this.findSystemTaskBeforeGroup(schema, groupId);

    if (existingSystemTask) {
      return {
        schema,
        systemTaskId: existingSystemTask.id,
      };
    }

    const groupNodes = this.getGroupNodes(schema, groupId);

    if (!groupNodes.length) {
      return {
        schema,
        systemTaskId: null,
      };
    }

    const groupNodeIds = new Set(groupNodes.map(({ id }) => id));
    const externalIncomingEdges = schema.edges.filter(
      (edge) =>
        groupNodeIds.has(edge.targetId) && !groupNodeIds.has(edge.sourceId),
    );

    if (!externalIncomingEdges.length) {
      return {
        schema,
        systemTaskId: null,
      };
    }

    const systemNode = this.flowSchemaService.createSystemTaskNode();
    const externalEdgeIds = new Set(externalIncomingEdges.map(({ id }) => id));
    const sourceIds = [
      ...new Set(externalIncomingEdges.map(({ sourceId }) => sourceId)),
    ];
    const targetIds = [
      ...new Set(externalIncomingEdges.map(({ targetId }) => targetId)),
    ];

    return {
      schema: {
        ...schema,
        nodes: [...schema.nodes, systemNode],
        edges: [
          ...schema.edges.filter((edge) => !externalEdgeIds.has(edge.id)),
          ...sourceIds.map((id) => ({
            id: this.flowSchemaService.createFlowId(),
            sourceId: id,
            targetId: systemNode.id,
          })),
          ...targetIds.map((id) => ({
            id: this.flowSchemaService.createFlowId(),
            sourceId: systemNode.id,
            targetId: id,
          })),
        ],
        returns: schema.returns ?? [],
      },
      systemTaskId: systemNode.id,
    };
  }

  isInGroup(schema: FlowSchema, nodeId: string): boolean {
    return !!this.flowSchemaService.getNodeById(schema, nodeId)
      ?.parallelGroupId;
  }

  addParallelNode(
    schema: FlowSchema,
    nodeId: string,
  ): { schema: FlowSchema; newNodeId: string } {
    const node = this.flowSchemaService.getNodeById(schema, nodeId);
    if (!node) return { schema, newNodeId: '' };

    const groupId = node.parallelGroupId ?? this.createGroupId();

    const markedSchema: FlowSchema = node.parallelGroupId
      ? schema
      : {
          ...schema,
          nodes: schema.nodes.map((schemaNode) =>
            schemaNode.id === nodeId
              ? { ...schemaNode, parallelGroupId: groupId }
              : schemaNode,
          ),
        };

    const incomingEdges = this.flowSchemaService.getIncomingEdges(
      markedSchema,
      nodeId,
    );

    const newNode: FlowNode = {
      ...this.flowSchemaService.createUserTaskNode(),
      parallelGroupId: groupId,
    };

    const groupNodes = this.getGroupNodes(markedSchema, groupId);
    const groupNodeIds = new Set(groupNodes.map(({ id }) => id));

    const referenceNode = groupNodes.find((groupNode) =>
      this.flowSchemaService
        .getOutgoingEdges(markedSchema, groupNode.id)
        .some((edge) => !groupNodeIds.has(edge.targetId)),
    );

    const newNodes = [...markedSchema.nodes, newNode];
    const newEdges = [...markedSchema.edges];

    const incomingEdge = incomingEdges[0];

    if (incomingEdge) {
      newEdges.push({
        ...incomingEdge,
        id: this.flowSchemaService.createFlowId(),
        targetId: newNode.id,
      });
    }

    if (referenceNode) {
      this.flowSchemaService
        .getOutgoingEdges(markedSchema, referenceNode.id)
        .forEach((edge) => {
          if (!groupNodeIds.has(edge.targetId)) {
            newEdges.push({
              ...edge,
              id: this.flowSchemaService.createFlowId(),
              sourceId: newNode.id,
            });
          }
        });
    }

    return {
      schema: this.flowSchemaService.insertSystemTasks({
        nodes: newNodes,
        edges: newEdges,
        returns: markedSchema.returns ?? [],
      }),
      newNodeId: newNode.id,
    };
  }

  removeFromGroup(schema: FlowSchema, nodeIdOrGroupId: string): FlowSchema {
    return this.flowSchemaService.removeFinishAfterStart(
      this.removeGroupNodes(schema, nodeIdOrGroupId),
    );
  }

  private removeGroupNodes(
    schema: FlowSchema,
    nodeIdOrGroupId: string,
  ): FlowSchema {
    const groupId =
      this.flowSchemaService.getNodeById(schema, nodeIdOrGroupId)
        ?.parallelGroupId ?? nodeIdOrGroupId;

    const groupNodes = this.getGroupNodes(schema, groupId);
    if (!groupNodes.length) return schema;

    const removedIds = new Set(
      groupNodes.some(({ id }) => id === nodeIdOrGroupId)
        ? [nodeIdOrGroupId]
        : groupNodes.map(({ id }) => id),
    );

    let newSchema: FlowSchema = {
      nodes: schema.nodes.filter(({ id }) => !removedIds.has(id)),
      edges: schema.edges.filter(
        (edge) =>
          !removedIds.has(edge.sourceId) && !removedIds.has(edge.targetId),
      ),
      returns: (schema.returns ?? []).filter(
        (edge) =>
          !removedIds.has(edge.sourceId) && !removedIds.has(edge.targetId),
      ),
    };

    const remaining = groupNodes.filter(({ id }) => !removedIds.has(id));

    if (remaining.length === 1) {
      const [lastGroupNode] = remaining;

      return this.flowSchemaService.insertSystemTasks(
        this.flowSchemaService.cleanupSystemTasks({
          ...newSchema,
          nodes: newSchema.nodes.map((node) =>
            node.id === lastGroupNode.id
              ? { ...node, parallelGroupId: undefined }
              : node,
          ),
        }),
      );
    }

    if (remaining.length) {
      return newSchema;
    }

    const groupNodeIds = new Set(groupNodes.map(({ id }) => id));
    const incomingEdge = this.flowSchemaService
      .getIncomingEdges(schema, groupNodes[0].id)
      .find((edge) => !groupNodeIds.has(edge.sourceId));
    const outgoingEdge = this.flowSchemaService
      .getOutgoingEdges(schema, groupNodes[0].id)
      .find((edge) => !groupNodeIds.has(edge.targetId));

    if (!incomingEdge || !outgoingEdge) {
      return this.flowSchemaService.insertSystemTasks(
        this.flowSchemaService.cleanupSystemTasks(newSchema),
      );
    }

    return this.flowSchemaService.insertSystemTasks(
      this.flowSchemaService.cleanupSystemTasks({
        ...newSchema,
        edges: [
          ...newSchema.edges,
          {
            ...incomingEdge,
            id: this.flowSchemaService.createFlowId(),
            targetId: outgoingEdge.targetId,
          },
        ],
      }),
    );
  }
}
